import React from "react";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FiShoppingCart } from "react-icons/fi";
import { IoMdHome } from "react-icons/io";
import { FaAngleRight, FaCheck } from "react-icons/fa6";
import Username from "../../Components/NavDrawer/Username";
import DrawerLogin from "../../Components/NavDrawer/DrawerLogin";
import ItemDetails from "../../Components/NavDrawer/ItemDetails";
import CategorieGroup from "../../Components/NavDrawer/CategorieGroup";
import NavStore from "../../Components/NavDrawer/Store";
import Whatsappquestions from "../../Components/NavDrawer/WhatsappQuestions";
import Policies from "../../Components/NavDrawer/Policies";
import Navfooter from "../../Components/NavDrawer/NavFooter";
import LogoutButton from "../../Components/NavDrawer/Logout";
import { Paths } from "../../Services/EndPoints";
import { createSlug, fetcher } from "../../Utils/HelperFunctions";

const NavDrawer = ({
  loggedIn = false,
  name,
  number,
  open = false,
  focusLoginTick = 0,
  onClose,
  onLogout,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const storeDetails = useSelector((s) => s.storeDetails);

  // One consolidated call returns all four sections: shop (clothing sub-cats),
  // shopClothingByCraft, jewelry (sub-cats), shopJewelryByCraft.
  const { data: navData } = useSWR(open ? Paths.nav_drawer : null, fetcher, {
    revalidateOnFocus: false,
  });
  const { data: aboutMe } = useSWR(open ? Paths.about_me : null, fetcher);
  const { data: cartCount = 0 } = useSWR(
    open && loggedIn ? Paths.get_cart_count : null,
    fetcher,
  );

  const go = (path) => {
    onClose?.();
    navigate(path);
  };

  // hct_id drives the listing page; the slug is cosmetic (that page self-corrects
  // it). Keep the clickedHealthConcern gender hint MyStore sets for fashion.
  const goCategory = (catSlug, item) => {
    if (catSlug === "womens-fashion" || catSlug === "mens-fashion")
      sessionStorage.setItem("clickedHealthConcern", catSlug);
    else sessionStorage.removeItem("clickedHealthConcern");
    go(`/shop/${catSlug}/${createSlug(item.name)}/${item.id}`);
  };

  const toSubItems = (list, catSlug) =>
    (list || []).map((it) => ({
      name: it.name,
      image: it.icon,
      onClick: () => goCategory(catSlug, it),
    }));

  const toCraftItems = (list) =>
    (list || []).map((c) => ({
      name: c.name,
      region: c.origin,
      image: c.image,
      onClick: () => go(`/craft/${createSlug(c.name)}/${c.id}`),
    }));

  const shopList = toSubItems(navData?.shop, "womens-fashion");
  const jewelryList = toSubItems(navData?.jewelry, "jewelry");
  const clothingCraftList = toCraftItems(navData?.shopClothingByCraft);
  const jewelryCraftList = toCraftItems(navData?.shopJewelryByCraft);

  const subline = aboutMe
    ? `A store by ${[aboutMe.firstName, aboutMe.lastName]
        .filter(Boolean)
        .join(" ")} · ${aboutMe.city}`
    : undefined;

  return (
    <div className="pb-[14px]">
      <Username
        loggedIn={loggedIn}
        name={name}
        number={number}
        onClose={onClose}
        logo={storeDetails?.storeUrl || aboutMe?.profileImageUrl}
        storeName={storeDetails?.storeName}
        subline={subline}
      />

      {loggedIn ? (
        <ItemDetails onNav={go} cartCount={cartCount} />
      ) : (
        <DrawerLogin open={open} focusPhoneTick={focusLoginTick} />
      )}

      {/* Home — active */}
      <div
        onClick={() => go("/")}
        className="flex items-center gap-[14px] bg-[#F4F5FF] rounded-[9999px] mx-[10px] px-[16px] py-[12px] text-[#4862EE] cursor-pointer"
      >
        <span className="w-[26px] grid place-items-center flex-none">
          <IoMdHome size={21} />
        </span>
        <span className="text-[15px] font-[400]">Home</span>
      </div>

      {/* Verified Handloom */}
      <div
        onClick={() => go("/verified-handloom")}
        className="flex items-center justify-between bg-[#FEEFEB] rounded-[10px] my-[8px] mx-[10px] px-[16px] py-[12px] cursor-pointer"
      >
        <div className="flex items-center gap-[14px] min-w-0">
          <span className="w-[26px] grid place-items-center flex-none text-[#FF512A]">
            <FaCheck size={18} />
          </span>
          <span className="text-[15px] font-[400] text-[#1F212E]">
            Verified Handloom{" "}
            <span className="text-[11px] font-[400] text-[#707078]">
              · authentic, no powerloom
            </span>
          </span>
        </div>
        <FaAngleRight className="text-[#707078] w-[16px] h-[16px] flex-none" />
      </div>

      {shopList.length > 0 && (
        <CategorieGroup Tittle={t("navDrawer.shop")} List={shopList} />
      )}
      {clothingCraftList.length > 0 && (
        <CategorieGroup
          Tittle={t("navDrawer.shopClothingByCraft")}
          List={clothingCraftList}
        />
      )}
      {jewelryList.length > 0 && (
        <CategorieGroup Tittle={t("navDrawer.jewelry")} List={jewelryList} />
      )}
      {jewelryCraftList.length > 0 && (
        <CategorieGroup
          Tittle={t("navDrawer.shopJewelryByCraft")}
          List={jewelryCraftList}
        />
      )}

      <NavStore onNav={go} />

      {/* signed-out only: standalone Cart */}
      {!loggedIn && (
        <>
          <div className="h-[1px] bg-[#E8EAF0] mx-[16px] my-[8px]" />
          <div
            onClick={() => go("/cart")}
            className="flex items-center gap-[14px] px-[16px] py-[12px] cursor-pointer"
          >
            <span className="w-[26px] grid place-items-center text-[#2C2C2C] flex-none">
              <FiShoppingCart size={21} />
            </span>
            <span className="text-[15px] font-[400] text-[#1F212E]">Cart</span>
          </div>
        </>
      )}

      <Whatsappquestions phone={aboutMe?.mobileNumber} />

      {/* signed-in only: Log Out */}
      {loggedIn && (
        <>
          <div className="h-[1px] bg-[#E8EAF0] mx-[16px] my-[8px]" />
          <LogoutButton onClick={onLogout} />
        </>
      )}

      <Policies onNav={go} />
      <Navfooter gstin={aboutMe?.gstin} />
    </div>
  );
};
export default NavDrawer;
